/**
 * Dashboard Analytics Hooks
 *
 * React Query hooks for fetching dashboard analytics data using ts-rest.
 * All response types are automatically inferred from the contract.
 */

import { analyticsQueryClient } from '@/lib/api/ts-rest-client';
import { queryKeys } from '@/lib/query-client';
import type { DashboardFilters } from '../types/analytics';
import { filtersToQueryParams } from '../types/analytics';

/**
 * Get refresh interval from environment
 */
const getRefreshInterval = (): number => {
  const interval = import.meta.env.VITE_DASHBOARD_REFRESH_INTERVAL_MS;
  return interval ? Number.parseInt(interval, 10) : 60000; // Default 60s
};

/**
 * Check if auto-refresh is enabled
 */
const isAutoRefreshEnabled = (): boolean => {
  const enabled = import.meta.env.VITE_DASHBOARD_AUTO_REFRESH;
  return enabled === undefined || enabled === 'true';
};

/**
 * Default query options for dashboard queries
 *
 * Analytics queries are expensive and slow, so we:
 * - Limit retries to avoid hammering the backend
 * - Use longer stale time (analytics data doesn't change frequently)
 * - Keep auto-refresh for user experience
 */
const dashboardQueryOptions = {
  // Cache analytics data for 2 minutes (balances freshness vs backend load)
  staleTime: 2 * 60 * 1000, // 2 minutes (was 30s)

  // Keep unused data in cache for 10 minutes
  gcTime: 10 * 60 * 1000,

  // Auto-refresh every 60 seconds (configurable via env)
  refetchInterval: (isAutoRefreshEnabled() ? getRefreshInterval() : false) as number | false,
  refetchIntervalInBackground: true,

  // Analytics queries can be slow - only retry once for transient failures
  // Prevents hammering slow/overloaded endpoints with multiple retries
  retry: 1,

  // Longer delay before retry (analytics queries take time)
  retryDelay: 3000, // 3 seconds (was exponential: 1s, 2s, 4s)
};

/**
 * Hook to fetch dashboard KPI metrics using ts-rest
 *
 * @param filters - Dashboard filters
 * @returns Query with type-safe DashboardKpisResponse from contract
 */
export function useDashboardKpis(filters: DashboardFilters) {
  const params = filtersToQueryParams(filters);
  const queryKey = queryKeys.dashboard.kpis(params);

  const query = analyticsQueryClient.getDashboardKpis.useQuery(
    queryKey,
    { query: params },
    {
      ...dashboardQueryOptions,
      queryKey,
    },
  );

  // Transform ts-rest response to extract body
  // Only return data if status is 200, otherwise return undefined so error state is triggered
  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Hook to fetch jobs analytics data using ts-rest
 *
 * @param filters - Dashboard filters
 * @returns Query with type-safe JobsAnalyticsResponse from contract
 */
export function useDashboardJobsAnalytics(filters: DashboardFilters) {
  const params = filtersToQueryParams(filters);
  const queryKey = queryKeys.dashboard.jobs(params);

  const query = analyticsQueryClient.getJobsAnalytics.useQuery(
    queryKey,
    { query: params },
    {
      ...dashboardQueryOptions,
      queryKey,
    },
  );

  // Transform ts-rest response to extract body
  // Only return data if status is 200, otherwise return undefined so error state is triggered
  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Hook to fetch performance analytics data using ts-rest
 *
 * @param filters - Dashboard filters
 * @returns Query with type-safe PerformanceAnalyticsResponse from contract
 */
export function useDashboardPerformance(filters: DashboardFilters) {
  const params = filtersToQueryParams(filters);
  const queryKey = queryKeys.dashboard.performance(params);

  const query = analyticsQueryClient.getPerformanceAnalytics.useQuery(
    queryKey,
    { query: params },
    {
      ...dashboardQueryOptions,
      queryKey,
    },
  );

  // Transform ts-rest response to extract body
  // Only return data if status is 200, otherwise return undefined so error state is triggered
  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

/**
 * Combined hook to fetch all dashboard analytics data
 *
 * This convenience hook fetches all three analytics endpoints
 * and returns their data together.
 *
 * @param filters - Dashboard filters
 */
export function useDashboardAnalytics(filters: DashboardFilters) {
  const kpis = useDashboardKpis(filters);
  const jobs = useDashboardJobsAnalytics(filters);
  const performance = useDashboardPerformance(filters);

  return {
    kpis,
    jobs,
    performance,
    // Aggregate loading state
    isLoading: kpis.isLoading || jobs.isLoading || performance.isLoading,
    // True if all queries are successful
    isSuccess: kpis.isSuccess && jobs.isSuccess && performance.isSuccess,
    // True if any query has an error
    hasError: kpis.isError || jobs.isError || performance.isError,
    // Refetch all queries
    refetchAll: async () => {
      await Promise.all([kpis.refetch(), jobs.refetch(), performance.refetch()]);
    },
  };
}
